<script setup lang="ts">
import axios from 'axios'
import { Link } from 'lucide-vue-next'
import ReviewPlatformsStoreSetupModal from './ReviewPlatformsStoreSetupModal.vue'
import type { Store } from '@/views/contests/Components/types/global'

const emits = defineEmits(['change'])

// TODO: filtrer côté backend (endpoint GET /review-platforms) pour ne pas renvoyer les plateformes
// gérées via le flow delivery (deliveroo, ubereats) qui ont leur propre onglet "Plateformes".
const DELIVERY_PLATFORM_SLUGS = ['deliveroo', 'ubereats']

const reviewPlatforms = ref<Record<string, any>[]>([])
const { stores } = useLocalStorage()
const fetchReviewPlatforms = async () => {
  const { data } = await axios.get('/review-platforms')
  reviewPlatforms.value = data.data.filter((p: { slug: string }) => !DELIVERY_PLATFORM_SLUGS.includes(p.slug))
}
fetchReviewPlatforms()

const isModalSetupOpen = ref(false)
const setUpStore = ref(null)
const setUpReviewPlatform = ref(null)
const setUpReviewManager = ref(null)

const isConnected = (store, platformSlug) => {
  let isConnected = false
  if (getActivatedPlatformByStore(store, platformSlug)) {
    isConnected = true
  }
  return isConnected
}

const findStoreReviewPlatormBySlug = (store: Store, slug: string) => {
  return store.storeReviewPlatforms.find((storeReviewPlatform) => storeReviewPlatform.platform.slug === slug)
}

const getActivatedPlatformByStore = (store: Store, slug: string) => {
  let platformActivated = findStoreReviewPlatormBySlug(store, slug)

  if (!platformActivated?.isActive) {
    platformActivated = null
  }

  return platformActivated
}
const setUpPlatform = (store, reviewPlatform) => {
  setUpReviewPlatform.value = reviewPlatform
  setUpStore.value = store
  const storeReviewPlatform = findStoreReviewPlatormBySlug(store, reviewPlatform.slug)
  if (storeReviewPlatform) {
    setUpReviewManager.value =
      storeReviewPlatform.reviewManager.slug === 'partoo' ? storeReviewPlatform.reviewManager : null
  } else {
    setUpReviewManager.value = null
  }
  isModalSetupOpen.value = true
}
const closeModalSetUp = () => {
  isModalSetupOpen.value = false
  emits('change')
}
</script>

<template>
  <div>
    <ul class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <li v-for="store in stores" :key="store.id" class="col-span-1 rounded-lg border bg-white">
        <div class="border-b">
          <h3 class="m-6 text-lg font-semibold">
            {{ store.name }}
          </h3>
        </div>
        <div class="p-6">
          <div
            v-for="reviewPlatform in reviewPlatforms"
            :key="reviewPlatform.id"
            class="my-4 flex w-full flex-wrap items-center sm:justify-between"
          >
            <div class="flex items-center">
              <img :src="`/images/reviews/${reviewPlatform.slug}.svg`" class="h-6 pr-4" />
              <h4 class="hidden font-medium text-muted-foreground sm:block">
                {{ reviewPlatform.name }}
              </h4>
            </div>
            <BaseChip
              v-if="isConnected(store, reviewPlatform.slug)"
              color="green"
              :text="$t('pages.review.reviewSettingsPage.connected')"
            />
            <Button v-else variant="ghost" @click="setUpPlatform(store, reviewPlatform)">
              <Link class="text-muted-foreground" />
              {{ $t('pages.review.reviewSettingsPage.setup') }}
            </Button>
          </div>
        </div>
      </li>
    </ul>
    <ReviewPlatformsStoreSetupModal
      :show="isModalSetupOpen"
      :store="setUpStore"
      :review-platform="setUpReviewPlatform"
      :review-manager="setUpReviewManager"
      @close="closeModalSetUp"
    />
  </div>
</template>
